import { Exclude, Expose, Type } from 'class-transformer';
import { AccountDTO, AccountType } from './account.dto';
import { PlanDTO } from './plan.dto';
import { ProfileDTO } from './profile.dto';
import { UserProfileDTO } from './user-profile.dto';
import { WorkspaceDTO } from './workspace.dto';
import { ChannelType } from './platform-account.dto';
import { SocialSetDTO } from './social-set.dto';

@Exclude()
export class UserDTO extends ProfileDTO {
  @Expose()
  id: number;

  @Expose()
  enabled: boolean;

  @Expose()
  identityToken: string;

  @Expose()
  @Type(() => AccountDTO)
  accounts: AccountDTO[];

  @Expose()
  @Type(() => WorkspaceDTO)
  workspaces: WorkspaceDTO[];

  @Expose()
  @Type(() => SocialSetDTO)
  socialSets: SocialSetDTO[];

  @Expose()
  @Type(() => PlanDTO)
  plan: PlanDTO;

  @Expose()
  @Type(() => UserProfileDTO)
  profile: UserProfileDTO;

  @Expose()
  createdAt: string;

  /**
   * Get the count for the current user's instagram account
   */
  get instagramManagedAccountsCount(): number {
    if (this.accounts && this.accounts.length) {
      return this.accounts.filter(
        (account) => account.type === AccountType.INSTAGRAM && account.isOwner && !account.isDummy
      ).length;
    }

    return 0;
  }

  /**
   * Get the count for the current user's tiktok account
   */
  get tiktokManagedAccountsCount(): number {
    let tiktokManagedAcc = 0;
    if (this.workspaces && this.workspaces.length) {
      this.workspaces.map((workspace) => {
        if (workspace.isOwner) {
          tiktokManagedAcc += workspace.channelCount[ChannelType.TIKTOK];
        }
      });
    }
    return tiktokManagedAcc;
  }

  /**
   * Get the count for the current user's pinterest account
   */
  get pinterestManagedAccountsCount(): number {
    if (this.accounts && this.accounts.length) {
      return this.accounts.filter((account) => account.type === AccountType.PINTEREST).length;
    }

    return 0;
  }

  /**
   * Get the count for the current user's facebook account
   */
  get facebookManagedAccountsCount(): number {
    let facebookManagedAcc = 0;
    if (this.workspaces && this.workspaces.length) {
      this.workspaces.map((workspace) => {
        if (workspace.isOwner) {
          facebookManagedAcc += workspace.channelCount[ChannelType.FACEBOOK];
        }
      });
    }
    return facebookManagedAcc;
  }

  /**
   * Get the count for the current user's facebook account
   */
  get twitterManagedAccountsCount(): number {
    let facebookManagedAcc = 0;
    if (this.workspaces && this.workspaces.length) {
      this.workspaces.map((workspace) => {
        if (workspace.isOwner) {
          facebookManagedAcc += workspace.channelCount[ChannelType.TWITTER];
        }
      });
    }
    return facebookManagedAcc;
  }

  get managedAccountCount(): number {
    const accountCount = this.accounts
      ? this.accounts.filter((account) => account.isOwner && !account.isPlatform).length
      : 0;
    let channelCount = 0;
    if (this.plan.isNewPlan()) {
      const channelWorkspaces = this.workspaces
        ? this.workspaces.filter((workspace) => workspace.isOwner)
        : null;
      if (channelWorkspaces.length) {
        channelWorkspaces.map((workspace) => {
          channelCount += Object.values(workspace.channelCount).reduce((a, b) => a + b, 0);
        });
      }
    }
    return accountCount + channelCount;
  }

  get canAddPinterestAccount(): boolean {
    if (this.plan.isNewPlan()) {
      // check set limit for custom plans, and account limit for PERSONAL plan (when setLimit is 0)
      return this.plan.setLimit > 0
        ? this.plan.setLimit > this.pinterestManagedAccountsCount
        : this.plan.accountLimit > this.managedAccountCount;
    } else {
      if (this.plan.accountLimit === 1) {
        return this.plan.accountLimit > this.pinterestManagedAccountsCount;
      }
    }

    return this.plan.accountLimit > this.managedAccountCount;
  }

  get canAddInstagramAccount(): boolean {
    if (this.plan.isNewPlan()) {
      // check set limit for custom plans, and account limit for PERSONAL plan (when setLimit is 0)
      return this.plan.setLimit > 0
        ? this.plan.setLimit > this.instagramManagedAccountsCount
        : this.plan.accountLimit > this.managedAccountCount;
    } else {
      if (this.plan.accountLimit === 1) {
        return this.plan.accountLimit > this.instagramManagedAccountsCount;
      }
    }
    return this.plan.accountLimit > this.managedAccountCount;
  }

  get canAddTiktokAccount(): boolean {
    if (this.plan.isNewPlan()) {
      // check set limit for custom plans, and account limit for PERSONAL plan (when setLimit is 0)
      return this.plan.setLimit > 0
        ? this.plan.setLimit > this.tiktokManagedAccountsCount
        : this.plan.accountLimit > this.managedAccountCount;
    } else {
      return false;
    }
  }

  get canAddFacebookAccount(): boolean {
    if (this.plan.isNewPlan()) {
      // check set limit for custom plans, and account limit for PERSONAL plan (when setLimit is 0)
      return this.plan.setLimit > 0
        ? this.plan.setLimit > this.facebookManagedAccountsCount
        : this.plan.accountLimit > this.managedAccountCount;
    } else {
      return false;
    }
  }

  get canAddTwitterAccount(): boolean {
    if (this.plan.isNewPlan()) {
      // check set limit for custom plans, and account limit for PERSONAL plan (when setLimit is 0)
      return this.plan.setLimit > 0
        ? this.plan.setLimit > this.twitterManagedAccountsCount
        : this.plan.accountLimit > this.managedAccountCount;
    } else {
      return false;
    }
  }

  get activeAccount(): AccountDTO {
    return this.accounts ? this.accounts.find((account) => account.isActive) : null;
  }
}
